import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { StatCard } from '@nucleus-commerce/admin-kit/layout';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { useStore } from '@/shared/store/store-context';

import { RangePicker } from '../components/range-picker';
import { useAuctionsReport } from '../hooks/use-auctions-report';
import { useReportRange } from '../hooks/use-report-range';

const COLUMNS = 9;

function AuctionsReport() {
  const { t } = useTranslation('reports');
  const store = useStore();
  const format = useFormat();
  const { range, setRange } = useReportRange();
  const query = useAuctionsReport(store.id, range);
  const pages = query.data?.pages ?? [];
  const report = pages[0];
  const rows = pages.flatMap((page) => page.items);
  const ratio = (value: number | null) =>
    value === null ? '—' : format.number(value, { maximumFractionDigits: 2 }) + '×';
  const summary = report?.summary;

  return (
    <div className="flex flex-col gap-4">
      <RangePicker range={range} onChange={setRange} />
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <div
            className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
            data-tour="reports.auctions.tiles"
          >
            <StatCard
              label={t('auctions.revenue')}
              value={summary ? format.money(summary.revenue) : '—'}
              detail={t('auctions.revenueDetail')}
              isLoading={query.isPending}
            />
            <StatCard
              label={t('auctions.sold')}
              value={summary ? format.number(summary.sold) : '—'}
              detail={summary ? t('auctions.soldDetail', { count: summary.ended }) : ''}
              isLoading={query.isPending}
            />
            <StatCard
              label={t('auctions.sellThrough')}
              value={summary?.sellThrough != null ? format.percent(summary.sellThrough) : '—'}
              detail={t('auctions.sellThroughDetail')}
              isLoading={query.isPending}
            />
            <StatCard
              label={t('auctions.hammerToStarting')}
              value={summary ? ratio(summary.averageHammerToStarting) : '—'}
              detail={
                summary
                  ? `${t('auctions.hammerToStartingDetail')} · ${t('auctions.bidders')}: ${
                      summary.averageBidders === null
                        ? '—'
                        : format.number(summary.averageBidders, { maximumFractionDigits: 1 })
                    }`
                  : ''
              }
              isLoading={query.isPending}
            />
          </div>
          {summary && summary.sold > 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="auction-channels">
              {t('auctions.channels')}:{' '}
              {(['bidding', 'buyNow', 'secondChance'] as const)
                .map((key) => `${t(`auctions.channel.${key}`)} ${summary.soldVia[key]}`)
                .join(' · ')}
            </p>
          ) : null}
          <Card className="gap-0 overflow-hidden py-0" data-tour="reports.auctions.table">
            <Table aria-label={t('auctions.table')}>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">{t('auctions.columns.lot')}</TableHead>
                  <TableHead>{t('auctions.columns.outcome')}</TableHead>
                  <TableHead className="text-right">{t('auctions.columns.starting')}</TableHead>
                  <TableHead className="text-right">{t('auctions.columns.hammer')}</TableHead>
                  <TableHead className="text-right">{t('auctions.columns.ratio')}</TableHead>
                  <TableHead className="text-right">{t('auctions.columns.bids')}</TableHead>
                  <TableHead className="text-right">{t('auctions.columns.bidders')}</TableHead>
                  <TableHead className="text-right">{t('auctions.columns.watchers')}</TableHead>
                  <TableHead className="pr-5 text-right">{t('auctions.columns.closed')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.isPending ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={COLUMNS} className="px-5">
                        <Skeleton className="h-5" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : rows.length > 0 ? (
                  rows.map((row) => (
                    <TableRow key={row.auctionId}>
                      <TableCell className="pl-5 font-medium">
                        <Link
                          to="/s/$storeId/auctions/$auctionId"
                          params={{ storeId: store.id, auctionId: row.auctionId }}
                          className="hover:underline"
                        >
                          {row.lotTitle}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {t(`auctions.outcome.${row.outcome}`)}
                        {row.soldVia ? (
                          <span className="text-muted-foreground">
                            {' '}
                            · {t(`auctions.soldVia.${row.soldVia}`)}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {format.money(row.startingPrice)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.hammerPrice ? format.money(row.hammerPrice) : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {ratio(row.hammerToStarting)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {format.number(row.bidCount)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {format.number(row.bidderCount)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {format.number(row.watcherCount)}
                      </TableCell>
                      <TableCell className="pr-5 text-right" title={format.utc(row.closedAt)}>
                        {format.date(row.closedAt)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={COLUMNS} className="text-muted-foreground h-24 text-center">
                      {t('auctions.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
          {query.hasNextPage ? (
            <Button
              type="button"
              variant="outline"
              className="self-center"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              {t('auctions.more')}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}

export function AuctionsReportPage() {
  return (
    <FeatureErrorBoundary name="reports.auctions">
      <AuctionsReport />
    </FeatureErrorBoundary>
  );
}
